import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { filterSetsByType } from '../utils/filterUtils';

/**
 * Set Selection Dropdown Component
 * Displays available sets in flat list (no year grouping)
 */
const SetSelectionDropdown = ({
    availableSets = [],
    selectedSets = [],
    onChange,
    selectedSetTypes = { R: false, B: false, RB: false }
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Filter sets based on selected set types
    const filteredSets = filterSetsByType(availableSets, selectedSetTypes);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleToggleSet = (set) => {
        const newSelected = selectedSets.includes(set)
            ? selectedSets.filter(s => s !== set)
            : [...selectedSets, set];
        onChange(newSelected);
    };

    const handleSelectAll = () => {
        onChange(filteredSets);
    };

    const handleClearAll = () => {
        onChange([]);
    };

    const selectedCount = selectedSets.length;
    const hasTypeFilter = selectedSetTypes.R || selectedSetTypes.B || selectedSetTypes.RB;

    return (
        <div className="space-y-2 relative" ref={dropdownRef}>


            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none hover:bg-gray-100"
            >
                <span className="text-sm font-medium text-gray-700">
                    {selectedCount > 0 ? `${selectedCount} set${selectedCount > 1 ? 's' : ''} selected` : 'Choose sets...'}
                </span>
                <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="p-2 border-b border-gray-100 space-y-1.5">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search sets..."
                                className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>
                        <div className="flex gap-1.5">
                            <button
                                type="button"
                                onClick={handleSelectAll}
                                className="flex-1 px-2 py-1 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                            >
                                Select All
                            </button>
                            <button
                                type="button"
                                onClick={handleClearAll}
                                className="flex-1 px-2 py-1 text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>

                    {/* Flat list - no year grouping */}
                    <div className="max-h-52 overflow-y-auto custom-scrollbar p-1.5">
                        {filteredSets.length > 0 ? (
                            filteredSets
                                .filter(set => set.toLowerCase().includes(searchTerm.toLowerCase()))
                                .map(set => {
                                    const isSelected = selectedSets.includes(set);
                                    const [, , type] = set.split('_');
                                    const typeColor = type === 'R' ? 'blue' : type === 'B' ? 'purple' : 'green';

                                    return (
                                        <label
                                            key={set}
                                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-md cursor-pointer transition-colors group"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleSet(set)}
                                                className={`w-3.5 h-3.5 border-gray-300 rounded focus:ring-2 focus:ring-${typeColor}-500 cursor-pointer text-${typeColor}-600`}
                                            />
                                            <span className="flex-1 text-xs font-medium text-gray-700 group-hover:text-gray-900">
                                                {set}
                                            </span>
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded bg-${typeColor}-50 text-${typeColor}-700`}>
                                                {type}
                                            </span>
                                        </label>
                                    );
                                })
                        ) : (
                            <div className="p-6 text-center text-xs text-gray-500">
                                {filteredSets.length === 0
                                    ? 'No sets available. Select set types above.'
                                    : 'No sets match your search.'}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #3B82F6;
          border-radius: 10px;
        }
      `}</style>
        </div>
    );
};

export default SetSelectionDropdown;
